import React from "react";
import { useAccount, useConnect, useDisconnect } from "wagmi";
import { useX402Pay } from "../../hooks/useX402Pay";
import { PayButton } from "./PayButton";
import { ReceiptCard } from "./ReceiptCard";

/**
 * Wallet-connection + x402 pay flow.
 *
 * Three screens inside one panel:
 *   1. Not connected → grid of connector buttons from wagmi.
 *   2. Connected + idle/signing/submitting → address pill + PayButton.
 *   3. Success → ReceiptCard (payment landed).
 *
 * Error from useX402Pay renders as an inline strip above the Pay
 * button; the button itself stays clickable so the user can retry.
 * We intentionally don't clear `error` on every render — the user
 * sees WHY their last attempt failed until they start a new one,
 * which gives the network + wallet combined error visibility.
 *
 * wagmi's `useConnect().connectors` is populated by the parent
 * `WagmiConfig` in App.js (already configured with @web3modal's
 * `defaultWagmiConfig` + our chain list). We don't re-construct
 * it here — the /pay/* routes live inside the same WagmiConfig
 * tree so these hooks just work.
 */
export function WalletPanel({ link }) {
  const { address, isConnected } = useAccount();
  const { connect, connectors, isLoading: isConnecting, pendingConnector } =
    useConnect();
  const { disconnect } = useDisconnect();
  const { status, error, receipt, payNow, reset } = useX402Pay(link);

  // Success: receipt replaces the entire panel. ReceiptCard owns
  // the "View on explorer" + "Back to site" actions.
  if (status === "success" && receipt) {
    return <ReceiptCard receipt={receipt} link={link} />;
  }

  if (!isConnected) {
    return (
      <div className="pay-panel">
        <h3 className="pay-panel__heading">Connect your wallet</h3>
        <p className="pay-panel__subheading">
          You'll sign a single authorization — no gas, no approval transaction.
        </p>
        <div className="pay-connectors">
          {connectors.map((connector) => (
            <button
              key={connector.id}
              className="pay-connector"
              onClick={() => connect({ connector })}
              disabled={!connector.ready || isConnecting}
            >
              {connector.name}
              {isConnecting &&
                pendingConnector?.id === connector.id &&
                " (connecting…)"}
              {!connector.ready && " (unavailable)"}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Connected: show the pay button + any error state.
  return (
    <div className="pay-panel">
      <h3 className="pay-panel__heading">Confirm payment</h3>
      <div className="pay-panel__subheading">
        Connected as{" "}
        <code>
          {address?.slice(0, 6)}…{address?.slice(-4)}
        </code>{" "}
        <button
          type="button"
          onClick={() => {
            reset();
            disconnect();
          }}
          style={{
            background: "transparent",
            border: "none",
            color: "#534ab7",
            cursor: "pointer",
            padding: 0,
            textDecoration: "underline",
            fontSize: "inherit",
          }}
        >
          Change
        </button>
      </div>

      {status === "error" && error && (
        <div className="pay-status pay-status--error">{error.message}</div>
      )}
      {status === "switching" && (
        <div className="pay-status pay-status--info">
          Switching your wallet to {link.network}…
        </div>
      )}
      {status === "signing" && (
        <div className="pay-status pay-status--info">
          Sign the authorization in your wallet to continue.
        </div>
      )}
      {status === "submitting" && (
        <div className="pay-status pay-status--info">
          Settling on-chain — this usually takes a few seconds.
        </div>
      )}

      <PayButton
        link={link}
        status={status}
        onClick={() => {
          if (status === "error") reset();
          payNow();
        }}
      />
    </div>
  );
}
